/**
 * Utility for scaling small font sizes and line heights in classified ad HTML.
 * 
 * Classified ads entered in AccountScout for print often use small fonts (6-8pt)
 * and tight line heights (95%) that are difficult to read when displayed online.
 * This utility detects and adjusts these values to improve readability.
 */

const MIN_FONT_SIZE_PT = 11;
const MIN_LINE_HEIGHT_PERCENT = 130; // Minimum line-height for comfortable online reading

/**
 * Scales font sizes and normalizes line heights in ad HTML for online readability.
 * 
 * Font scaling:
 * - If the minimum font size is below 9pt, all fonts are scaled proportionally
 * - Example: 6pt and 8pt become 9pt and 12pt (scale factor 1.5)
 * 
 * Line height normalization:
 * - Percentage line-heights below 130% are increased to 130%
 * - This improves readability for text designed for tight print layouts
 * 
 * @param html - The ad HTML containing inline styles
 * @returns The HTML with adjusted font sizes and line heights
 */
export function scaleAdFontSizes(html: string): string {
  if (!html) return html;
  
  let result = html;
  
  // === 1. Scale font sizes ===
  const fontSizeRegex = /font-size:\s*(\d+(?:\.\d+)?)(pt|px)/gi;
  const fontMatches = [...result.matchAll(fontSizeRegex)];
  
  if (fontMatches.length > 0) {
    // Convert all sizes to points and find the minimum
    const sizesInPt = fontMatches.map(m => {
      const value = parseFloat(m[1]);
      const unit = m[2].toLowerCase();
      // 1px ≈ 0.75pt (at 96dpi, 72pt per inch)
      return unit === 'px' ? value * 0.75 : value;
    });
    
    const minSize = Math.min(...sizesInPt);
    
    // Scale if minimum is below threshold
    if (minSize < MIN_FONT_SIZE_PT) {
      const scaleFactor = MIN_FONT_SIZE_PT / minSize;
      
      result = result.replace(fontSizeRegex, (_match, value, unit) => {
        const numValue = parseFloat(value);
        const inPt = unit.toLowerCase() === 'px' ? numValue * 0.75 : numValue;
        const scaled = Math.round(inPt * scaleFactor);
        return `font-size:${scaled}pt`;
      });
    }
  }
  
  // === 2. Normalize line heights ===
  // Match line-height with percentage values (e.g., line-height:95%)
  const lineHeightRegex = /line-height:\s*(\d+(?:\.\d+)?)%/gi;
  
  result = result.replace(lineHeightRegex, (_match, value) => {
    const numValue = parseFloat(value);
    // If line-height is below minimum, increase it
    if (numValue < MIN_LINE_HEIGHT_PERCENT) {
      return `line-height:${MIN_LINE_HEIGHT_PERCENT}%`;
    }
    return `line-height:${numValue}%`;
  });
  
  return result;
}
